import { v4 as uuid } from 'uuid'
import { encryptJSON, decryptJSON } from '../crypto/records'
import { db } from './schema'
import type {
  BookContent,
  GarmentContent,
  ProjectContent,
  PatternContent,
  SizeChartContent,
  SettingsContent,
} from '../types/records'

export interface ListItem<T> {
  id: string
  createdAt: number
  content: T
}

export interface ProjectListItem extends ListItem<ProjectContent> {
  garmentId: string
  type: 'made-to-measure' | 'size-chart'
  updatedAt: number
}

export class BookRepository {
  constructor(private dek: Uint8Array) {}

  async create(content: BookContent): Promise<string> {
    const id = uuid()
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.books.put({ id, encryptedPayload, createdAt: Date.now() })
    return id
  }

  async get(id: string): Promise<BookContent | null> {
    const row = await db.books.get(id)
    if (!row) return null
    return decryptJSON<BookContent>(row.encryptedPayload, this.dek)
  }

  async list(): Promise<ListItem<BookContent>[]> {
    const rows = await db.books.orderBy('createdAt').toArray()
    return Promise.all(
      rows.map(async r => ({
        id: r.id,
        createdAt: r.createdAt,
        content: await decryptJSON<BookContent>(r.encryptedPayload, this.dek),
      })),
    )
  }

  async update(id: string, content: BookContent): Promise<void> {
    const row = await db.books.get(id)
    if (!row) throw new Error('Libro non trovato')
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.books.put({ ...row, encryptedPayload })
  }

  async delete(id: string): Promise<void> {
    await db.books.delete(id)
  }
}

export class GarmentRepository {
  constructor(private dek: Uint8Array) {}

  async create(bookId: string, content: GarmentContent): Promise<string> {
    const id = uuid()
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.garments.put({ id, bookId, encryptedPayload, createdAt: Date.now() })
    return id
  }

  async get(id: string): Promise<GarmentContent | null> {
    const row = await db.garments.get(id)
    if (!row) return null
    return decryptJSON<GarmentContent>(row.encryptedPayload, this.dek)
  }

  async list(): Promise<ListItem<GarmentContent>[]> {
    const rows = await db.garments.orderBy('createdAt').toArray()
    return Promise.all(
      rows.map(async r => ({
        id: r.id,
        createdAt: r.createdAt,
        content: await decryptJSON<GarmentContent>(r.encryptedPayload, this.dek),
      })),
    )
  }

  async listByBook(bookId: string): Promise<ListItem<GarmentContent>[]> {
    const rows = await db.garments.where('bookId').equals(bookId).toArray()
    return Promise.all(
      rows.map(async r => ({
        id: r.id,
        createdAt: r.createdAt,
        content: await decryptJSON<GarmentContent>(r.encryptedPayload, this.dek),
      })),
    )
  }

  async delete(id: string): Promise<void> {
    await db.garments.delete(id)
  }
}

export class ProjectRepository {
  constructor(private dek: Uint8Array) {}

  async create(
    garmentId: string,
    type: 'made-to-measure' | 'size-chart',
    content: ProjectContent,
  ): Promise<string> {
    const id = uuid()
    const encryptedPayload = await encryptJSON(content, this.dek)
    const now = Date.now()
    await db.projects.put({
      id,
      garmentId,
      type,
      encryptedPayload,
      createdAt: now,
      updatedAt: now,
    })
    return id
  }

  async get(id: string): Promise<ProjectContent | null> {
    const row = await db.projects.get(id)
    if (!row) return null
    return decryptJSON<ProjectContent>(row.encryptedPayload, this.dek)
  }

  async list(): Promise<ProjectListItem[]> {
    const rows = await db.projects.orderBy('updatedAt').reverse().toArray()
    return Promise.all(
      rows.map(async r => ({
        id: r.id,
        garmentId: r.garmentId,
        type: r.type,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
        content: await decryptJSON<ProjectContent>(r.encryptedPayload, this.dek),
      })),
    )
  }

  async update(id: string, content: ProjectContent): Promise<void> {
    const row = await db.projects.get(id)
    if (!row) throw new Error('Progetto non trovato')
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.projects.put({ ...row, encryptedPayload, updatedAt: Date.now() })
  }

  async delete(id: string): Promise<void> {
    await db.projects.delete(id)
  }
}

export class PatternRepository {
  constructor(private dek: Uint8Array) {}

  async create(projectId: string, content: PatternContent): Promise<string> {
    const id = uuid()
    const encryptedPayload = await encryptJSON(content, this.dek)
    const existing = await db.patterns.where('projectId').equals(projectId).count()
    const version = existing + 1
    await db.patterns.put({
      id,
      projectId,
      version,
      encryptedPayload,
      createdAt: Date.now(),
    })
    return id
  }

  async listByProject(
    projectId: string,
  ): Promise<Array<ListItem<PatternContent> & { version: number }>> {
    const rows = await db.patterns.where('projectId').equals(projectId).sortBy('version')
    return Promise.all(
      rows.map(async r => ({
        id: r.id,
        version: r.version,
        createdAt: r.createdAt,
        content: await decryptJSON<PatternContent>(r.encryptedPayload, this.dek),
      })),
    )
  }

  async getLatest(
    projectId: string,
  ): Promise<{ id: string; version: number; content: PatternContent } | null> {
    const rows = await db.patterns.where('projectId').equals(projectId).sortBy('version')
    const last = rows[rows.length - 1]
    if (!last) return null
    return {
      id: last.id,
      version: last.version,
      content: await decryptJSON<PatternContent>(last.encryptedPayload, this.dek),
    }
  }

  async delete(id: string): Promise<void> {
    await db.patterns.delete(id)
  }
}

export class SizeChartRepository {
  constructor(private dek: Uint8Array) {}

  async create(garmentId: string, content: SizeChartContent): Promise<string> {
    const id = uuid()
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.sizeCharts.put({ id, garmentId, encryptedPayload, createdAt: Date.now() })
    return id
  }

  async getByGarment(
    garmentId: string,
  ): Promise<{ id: string; content: SizeChartContent } | null> {
    const row = await db.sizeCharts.where('garmentId').equals(garmentId).first()
    if (!row) return null
    return {
      id: row.id,
      content: await decryptJSON<SizeChartContent>(row.encryptedPayload, this.dek),
    }
  }

  async update(id: string, content: SizeChartContent): Promise<void> {
    const row = await db.sizeCharts.get(id)
    if (!row) throw new Error('Piano taglie non trovato')
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.sizeCharts.put({ ...row, encryptedPayload })
  }

  async delete(id: string): Promise<void> {
    await db.sizeCharts.delete(id)
  }
}

export class SettingsRepository {
  constructor(private dek: Uint8Array) {}

  async get(): Promise<SettingsContent | null> {
    const row = await db.settings.get('singleton')
    if (!row) return null
    return decryptJSON<SettingsContent>(row.encryptedPayload, this.dek)
  }

  async set(content: SettingsContent): Promise<void> {
    const encryptedPayload = await encryptJSON(content, this.dek)
    await db.settings.put({ id: 'singleton', encryptedPayload })
  }

  async clear(): Promise<void> {
    await db.settings.delete('singleton')
  }
}
